/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React, { useState } from "react";
import {
	Table,
	TableHead,
	TableCell,
	TableRow,
	TableBody,
	TableSortLabel,
	IconButton,
	Tooltip,
	withStyles,
	TablePagination,
} from "@material-ui/core";
import { Check, EditOutlined } from "@material-ui/icons";
import usePagination from "../../../../hooks/usePagination";

const headerCells = [
	{ id: "username", numeric: false, disablePadding: false, label: "Username", sortable: true },
	{ id: "extension", numeric: false, disablePadding: false, label: "Extension", sortable: true },
	{ id: "encrypt", numeric: false, disablePadding: false, label: "Encrypt", sortable: false },
	{ id: "transcribe", numeric: false, disablePadding: false, label: "Transcribe", sortable: false },
	{ id: "deliveryOptions", numeric: false, disablePadding: false, label: "Delivery Options", sortable: false },
];

const AgentsSortOrder = {
	ASC: "asc",
	DESC: "desc",
};

const StyledTableCell = withStyles((_theme) => ({
	root: {
		"&:nth-child(1)": {
			width: "30% !important",
		},
	},
	body: {
		verticalAlign: "middle !important",
		fontSize: 14,
		padding: "10px 24px 10px 16px",
		"& p": {
			whiteSpace: "pre-line",
			margin: 0,
			maxHeight: "180px",
			overflowY: "hidden",
			textOverflow: "ellipsis",
		},
	},
}))(TableCell);

/**
 * Component used to manage agents
 * @param {Object} agents contains the agent details.
 * @param {Function} editAgent function use to get the agent details for editing.
 * @param {String}searchTerm use to search the agents based on extension and username
 * @param {Object} globalSettings contains the global configurations
 */
export const AgentExtensionsTable = ({ agents, editAgent, searchTerm, globalSettings }) => {
	const [sortAgent, setSortAgent] = useState({ sortKey: "username", sortOrder: "asc" });
	const [agentList, setAgentList] = useState(null);

	const { setPageNo, setRowsPerPage, pageNo, dataSlice, rowsPerPage } = usePagination(agentList || []);

	React.useEffect(() => {
		if (agents && searchTerm) {
			const searchMatches =
				agents &&
				agents.filter(
					(agent) =>
						agent.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
						(agent.extension && agent.extension.toLowerCase().includes(searchTerm.toLowerCase())),
				);
			setAgentList(searchMatches);
		} else {
			setAgentList(agents);
		}
	}, [agents, searchTerm]);

	const sortBy = (id) => {
		const sortAgents = [...agentList];
		let header = headerCells?.find((item) => item.id === id);
		if (header.sortable) {
			sortAgents.sort((a, b) => {
				if (id === "username") {
					return sortAgent.sortOrder === AgentsSortOrder.ASC
						? a.username.toLowerCase() > b.username.toLowerCase()
							? 1
							: -1
						: a.username.toLowerCase() < b.username.toLowerCase()
						? 1
						: -1;
				}
				if (id === "extension") {
					return sortAgent.sortOrder === AgentsSortOrder.ASC
						? a.extension > b.extension
							? 1
							: -1
						: a.extension < b.extension
						? 1
						: -1;
				}
				return sortAgents;
			});
		}
		return sortAgents;
	};

	return (
		<div>
			<Table size="small">
				<TableHead>
					<TableRow>
						{headerCells.map((column) => (
							<TableCell key={column.label} style={{ padding: "24px 24px 12px 16px" }}>
								{column.sortable ? (
									<TableSortLabel
										active={sortAgent.sortKey === column.field}
										direction={sortAgent.sortOrder === column.field ? sortAgent.sortOrder : "asc"}
										onClick={() => {
											setAgentList(sortBy(column.id));
											sortAgent.sortOrder === AgentsSortOrder.ASC
												? setSortAgent({ sortOrder: "desc", sortKey: column.id })
												: setSortAgent({ sortOrder: "asc", sortKey: column.id });
										}}
									>
										{column.label}
									</TableSortLabel>
								) : (
									column.label
								)}
							</TableCell>
						))}
						<TableCell style={{ width: 100 }}></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{dataSlice &&
						dataSlice.map((agent) => (
							<TableRow key={agent.agentId}>
								<StyledTableCell align="left">{agent.username}</StyledTableCell>
								<StyledTableCell align="left">{agent.extension}</StyledTableCell>
								<StyledTableCell align="center">
									{agent.encryptVoicemail && (
										<Check fontSize="inherit" style={{ opacity: globalSettings.encryptVoicemail ? 1 : 0.4 }} />
									)}
								</StyledTableCell>
								<StyledTableCell align="center">
									{agent.transcribeVoicemail && (
										<Check style={{ opacity: globalSettings.transcribeVoicemail ? 1 : 0.4 }} fontSize="inherit" />
									)}
								</StyledTableCell>
								<StyledTableCell align="left">
									<span style={{ paddingRight: agent.deliveryOptions.email ? "10px" : "0px" }}>
										{agent.deliveryOptions.email ? "Email" : ""}
									</span>
									<span>
										{agent.deliveryOptions.sms.enabled ? `SMS: ${agent.deliveryOptions.sms.phoneNumber}` : ""}
									</span>
								</StyledTableCell>
								<StyledTableCell align="right">
									<IconButton
										size="small"
										onClick={async () => {
											const editableAgentObject = {
												agentId: agent.agentId,
												transcribeVoicemail: agent.transcribeVoicemail,
												encryptVoicemail: agent.encryptVoicemail,
												extension: agent.extension,
												deliverEmail: agent.deliveryOptions.email,
												deliverSMS: agent.deliveryOptions.sms.enabled,
												deliverSMSPhoneNumber:
													agent.deliveryOptions.sms.phoneNumber === null ? "" : agent.deliveryOptions.sms.phoneNumber,
												username: agent.username,
											};
											await editAgent(editableAgentObject);
										}}
									>
										<Tooltip title="Edit">
											<EditOutlined />
										</Tooltip>
									</IconButton>
								</StyledTableCell>
							</TableRow>
						))}
				</TableBody>
			</Table>
			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				component="div"
				count={agentList?.length || 0}
				rowsPerPage={rowsPerPage}
				page={pageNo}
				onPageChange={(e, pageNo) => setPageNo(pageNo)}
				onRowsPerPageChange={(rowsPerPageCnt) => {
					setRowsPerPage(parseInt(rowsPerPageCnt.currentTarget.innerText) || 10);
					setPageNo(0);
				}}
			/>
		</div>
	);
};
